import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { useRootStore } from '../../../../stores/rootStore';

const SOLD_METRICS = [
  {
    metricId: '3',
    displayName: 'Average GPO Distance',
    definition:
      'The average distance from the GPO of those properties that transacted over the last 12 months.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '4',
    displayName: ' Average Hold Period (years)',
    definition:
      'The average number of years a property has been held between sales. The calculation includes all properties sold over the last year and is simply the difference between the most recent date of sale and the previous date of sale.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '5',
    displayName: 'Average Land Area',
    definition:
      'The average size of land of those properties that transacted over the last 12 months.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '12',
    displayName: 'Annual Change in Median Price (10 years)',
    definition:
      'The compounding annual change in median sale price based on the current period compared with the same period 10 years ago. The calculation indicates the average annual growth rate and is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '13',
    displayName: 'Annual Change in Median Price (20 years)',
    definition:
      'The compounding annual change in median sale price based on the current period compared with the same period 20 years ago. The calculation indicates the average annual growth rate and is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '14',
    displayName: 'Annual Change in Median Price (5 years)',
    definition:
      'The compounding annual change in median sale price based on the current period compared with the same period 5 years ago. The calculation indicates the average annual growth rate and is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '19',
    displayName: 'Change in Median Price (5 years)',
    definition:
      'The difference between the median sale prices in the current period (last 12 months) compared to the same period five years ago. The result is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '20',
    displayName: 'Median Vendor Discount (12 months) ',
    definition:
      'The median difference between the contract price on a property and the first advertised price. The figure is expressed as a percentage and is an average of all private treaty sales which sold for less than their initial asking price during the last 12 months. Auction listings and listings without an advertised asking price are excluded from the calculation.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '21',
    displayName: 'Median Sale Price (12 months)',
    definition:
      'The middle sale price of all transactions recorded during the 12 month period. Note that sale prices lower than $10,000 and higher than $80,000,000 are excluded from the analysis.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '22',
    displayName: 'Median Sale Price (350K to 750K)',
    definition:
      'The middle price of all transactions above $350,000 and under $750,000 recorded during the 12 month period.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '23',
    displayName: 'Median Sale Price (6 months)',
    definition:
      'The middle sale price of all transactions recorded during the 6 month period. Note that sale prices lower than $10,000 and higher than $80,000,000 are excluded from the analysis.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '24',
    displayName: 'Median Sale Price (over 750K)',
    definition:
      'The middle price of all transactions above $750,000 recorded during the 12 month period.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '25',
    displayName: 'Median Sale Price (past 3 months)',
    definition:
      'The middle sale price of all transactions recorded during the 3 month period. Note that sale prices lower than $10,000 and higher than $80,000,000 are excluded from the analysis.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '26',
    displayName: ' Median Sale Price (under 350K)',
    definition:
      'The middle price of all transactions under $350,000 recorded during the 12 month period.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '28',
    displayName: 'Median Land Size (12 months)',
    definition:
      'The median size in square meters of all blocks of vacant land that have sold over the past year.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '29',
    displayName: 'Median Land Size (3 months)',
    definition:
      'The median size in square meters of all blocks of vacant land that have sold over the past three months.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '30',
    displayName: 'Median Land Price per SQM (12 months)',
    definition:
      'The median price per square meter for vacand land sold over the past 12 months.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '31',
    displayName: 'Median Land Price per SQM (3 months)',
    definition:
      'The median price per square meter for vacand land sold over the past 3 months.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '32',
    displayName: 'Median Days on Market (12 months)',
    definition:
      'The median number of days it has taken to sell those properties sold by private treaty sale during the last 12 months. The calculation excludes auction listings and listings where an asking price is not advertised. The days on market calculation uses the contract date on the property compared with the first advertised date.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '35',
    displayName: ' Change in Sales Volumes (monthly)',
    definition:
      'The percentage change in sales volumes within the specified region in the current period compared to the same period 1 month ago. The result is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '37',
    displayName: 'Number Sold (12 months)',
    definition:
      'A count of all transactions within the specified region over the last 12 months.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '38',
    displayName: 'Number Sold (6 Months)',
    definition:
      'A count of all transactions within the specified region over the last 6 months.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '39',
    displayName: 'Number Sold (monthly)',
    definition:
      'A count of all transactions within the specified region over the last month.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '40',
    displayName: 'Number Sold (past 3 months)',
    definition:
      'A count of all transactions within the specified region over the last 3 months.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '42',
    displayName: 'Lower Quartile (12 months)',
    definition:
      'The 25th percentile sale price of sales over the past 12 months within the geography.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '43',
    displayName: 'Upper Quartile (12 months)',
    definition:
      'The 75th percentile sale price of sales over the past 12 months within the geography.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '45',
    displayName: 'Change in Sales Volumes (3 months)',
    definition:
      'The percentage change in sales volumes within the specified region in the current period compared to the same period 3 months ago. The result is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '46',
    displayName: 'Change in Median Price (quarter)',
    definition:
      'The difference between the median sale price in the current period (last 12 months) compared to the same period 3 months ago. The result is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '51',
    displayName: 'Number Sold between $1m and $2m (12 months)',
    definition:
      'A count of all transactions within the specified region over the last 12 months that have sold for between $1,000,000 and $1,999,999.99.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '52',
    displayName: ' Number Sold between $200k and $400k (12 months) ',
    definition:
      'A count of all transactions within the specified region over the last 12 months that have sold for between $200,000 and $399,999.99.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '53',
    displayName: 'Number Sold above $2m (12 months)',
    definition:
      'A count of all transactions within the specified region over the last 12 months that have sold for more than $2,000,000.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '54',
    displayName: 'Number Sold for less than $200k (12 months)',
    definition:
      'A count of all transactions within the specified region over the last 12 months that have sold for less than $200,000.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '55',
    displayName: ' Number Sold between $400k and $600k (12 months) ',
    definition:
      'A count of all transactions within the specified region over the last 12 months that have sold for between $400,000 and $599,999.99.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '56',
    displayName: ' Number Sold between $600k and $800k (12 months) ',
    definition:
      'A count of all transactions within the specified region over the last 12 months that have sold for between $600,000 and $899,999.99.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '57',
    displayName: 'Number Sold between $800k and $1m (12 months)',
    definition:
      'A count of all transactions within the specified region over the last 12 months that have sold for between $800,000 and $999,999.99.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '61',
    displayName: 'Change in Median Price (3 years)',
    definition:
      'The difference between the median sale prices in the current period (last 12 months) compared to the same period three years ago. The result is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '66',
    displayName: 'Total Value of Sales (12 months)',
    definition:
      'The total value of all property transactions recorded over the past year within the geography.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '67',
    displayName: 'Total Value of Sales (monthly)',
    definition:
      'The total value of all property transactions recorded over the past month within the geography.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '68',
    displayName: 'Stock Turnover (12 months)',
    definition:
      'The percentage of total dwellings within the specified region that have sold over the last 12 months.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '69',
    displayName: 'Change in Median Price (12 months)',
    definition:
      'The difference between the median sale prices in the current period (last 12 months) compared to the same period 12 months ago. The result is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: true,
  },
  {
    metricId: '71',
    displayName: 'Change in Sales Volumes (12 months)',
    definition:
      'The percentage change in sales volumes within the specified region in the current period compared to the same period 12 months ago. The result is expressed as a percentage.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '75',
    displayName: 'Median Days on Market (12 months)',
    definition:
      'The median number of days it has taken to sell all properties during the last 12 months. The days on market calculation uses the contract date on the property compared with the first advertised date.',
    isAvailableInAU: false,
    isAvailableInNZ: true,
  },
  {
    metricId: '82',
    displayName: 'Sale price / CV ratio (3 months)',
    definition:
      'The percentage difference between sale price and CV over the past 3 months',
    isAvailableInAU: false,
    isAvailableInNZ: true,
  },
  {
    metricId: '95',
    displayName: 'Cotality RP Data Mortgage Index (RMI)',
    definition:
      'Cotality systems monitor more than 100,000 mortgage activity events ever month across our four main finance industry platforms. Monitoring the activity events across this platform provides a unique and timely lead indicator to housing finance commitments.',
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '96',
    displayName: ' Cotality RP Data Mortgage Index (RMI) Change (12 months) ',
    definition:
      "Cotality systems monitor more than 100,000 mortgage activity events ever month across our four main finance industry platforms. Monitoring the activity events across this platform provides a unique and timely lead indicator to housing finance commitments. The 12 month change compares the change in the index value over the last 12 months. The data provided is mapped to the suburbs corresponding sub-region. When an insufficient number of events have been recorded, no 12 month change will be calculated and the field will be recorded as 'n.a.'.",
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '97',
    displayName:
      ' Total Cotality RP Data Mortgage Index (RMI) Events (per client) ',
    definition:
      "The total count over the past four weeks of valuations run across Cotality's four main finance industry platforms divided by total clients. When an insufficient number of events have been recorded, 'n.a.' will be displayed. <br /> <br /> <br /> <b>Unavailable geographies: National, State, Suburb</b>",
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
  {
    metricId: '98',
    displayName: ' Total Cotality RP Data Mortgage Index (RMI) Events ',
    definition:
      "The total count over the past four weeks of valuations run across Cotality's four main finance industry platforms. When an insufficient number of events have been recorded, 'n.a.' will be displayed. <br /> <br /> <br /> <b>Unavailable geographies: National, State, Suburb</b>",
    isAvailableInAU: true,
    isAvailableInNZ: false,
  },
];

export const SoldMetrics = () => {
  const country = useRootStore(state => state.country);

  return (
    <>
      <Typography id="sold-metrics" variant="h6" fontWeight={600} my={0.5}>
        Sold Metrics
      </Typography>

      <Typography variant="body2">
        The Sold metrics provide statistics based off property sales.
        <br />
        <br />
        Sales-based metrics are lagged. Meaning they are current as of the
        latest reporting period minus 3 months.
      </Typography>

      <br />

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ whiteSpace: 'nowrap' }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Definition</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SOLD_METRICS.filter(metric =>
              country === 'au'
                ? metric.isAvailableInAU
                : metric.isAvailableInNZ,
            ).map(metric => (
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {metric.metricId}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {metric.displayName}
                </TableCell>
                <TableCell
                  dangerouslySetInnerHTML={{ __html: metric.definition }}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
